import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.dashboard');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

interface DashboardCard {
    label: string;
    value: number;
    description: string;
    marker: string;
    accentClass: string;
    markerClass: string;
}

interface PipelineCounts {
    sourceDocuments: number;
    activeDocumentVersions: number;
    activeChunks: number;
    extractionRuns: number;
    successfulRuns: number;
    failedRuns: number;
    candidate: number;
    needsReview: number;
    approved: number;
    rejected: number;
}

@Component({
    selector: 'wiz-page-dashboard',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.dashboard/view.scss */
:host {
  display: block;
  min-height: 100%;
}`],
})
export class PageDashboardComponent implements OnInit {
    public loading: boolean = true;
    public errorMessage: string = '';
    public stats: DashboardCard[] = [];
    public recentActivity: any[] = [];
    public lastConnection: any = null;
    public recentProfile: any = null;
    public knowledge: any = {};
    public activeOntology: any = null;
    public mysqlStatus: string = 'unknown';
    public pipelineCounts: PipelineCounts = {
        sourceDocuments: 0,
        activeDocumentVersions: 0,
        activeChunks: 0,
        extractionRuns: 0,
        successfulRuns: 0,
        failedRuns: 0,
        candidate: 0,
        needsReview: 0,
        approved: 0,
        rejected: 0
    };
    public lastUpdated: string = '';

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.load();
    }

    public async load() {
        this.loading = true;
        this.errorMessage = '';
        await this.service.render();

        try {
            const response = await wiz.call("overview");
            if (response.code !== 200) {
                this.errorMessage = response.data?.message || "대시보드 정보를 불러오지 못했습니다.";
                return;
            }

            const payload = response.data || {};
            this.stats = this.buildCards(payload);
            this.applyPipelinePayload(payload);
            this.recentActivity = this.toArray(
                payload.recent_activity || payload.recent || payload.activities
            );
            this.lastConnection = payload.last_connection || payload.latest_connection || null;
            this.recentProfile = payload.recent_profile || payload.latest_profile || null;
            this.lastUpdated = new Date().toLocaleString('ko-KR', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (_error) {
            this.errorMessage = "대시보드 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.";
        } finally {
            this.loading = false;
            await this.service.render();
        }
    }

    public get hasSourceData(): boolean {
        return this.stats.some((item) => item.value > 0);
    }

    public formatNumber(value: number): string {
        return Number(value || 0).toLocaleString('ko-KR');
    }

    public activityTitle(item: any): string {
        return item?.title || item?.label || item?.message || item?.action || "데이터 변경";
    }

    public activityDetail(item: any): string {
        const values: string[] = [];
        const detail = item?.detail || item?.description;
        const status = item?.status || item?.connection_status;
        const date = item?.updated_at || item?.created_at || item?.date;
        if (detail) values.push(String(detail));
        if (status) values.push(this.statusLabel(status));
        if (date) values.push(this.formatDate(date));
        return values.length > 0 ? values.join(" · ") : "원문·출처 계층";
    }

    public connectionStatusLabel(): string {
        if (!this.lastConnection) return "연결 기록 없음";
        return this.statusLabel(
            this.lastConnection.status || this.lastConnection.connection_status
        );
    }

    public connectionStatusClass(): string {
        const status = String(
            this.lastConnection?.status || this.lastConnection?.connection_status || ""
        ).toLowerCase();
        if (["connected", "ready", "success", "ok"].includes(status)) {
            return "bg-emerald-50 text-emerald-700";
        }
        if (["failed", "error", "disconnected"].includes(status)) {
            return "bg-red-50 text-red-700";
        }
        return "bg-slate-100 text-slate-600";
    }

    public formatDate(value: any): string {
        if (!value) return "기록 없음";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    public profileCount(keys: string[]): number {
        if (!this.recentProfile) return 0;
        return this.pickCount(this.recentProfile, keys);
    }

    public profilePercent(key: string): string {
        const scopes = [
            this.recentProfile,
            this.recentProfile?.summary,
            this.recentProfile?.coverage
        ];
        let value: any = 0;
        for (const scope of scopes) {
            if (scope?.[key] !== undefined && scope?.[key] !== null) {
                value = scope[key];
                break;
            }
        }
        const numberValue = Number(value || 0);
        const percent = numberValue >= 0 && numberValue <= 1
            ? numberValue * 100
            : numberValue;
        return Math.max(0, Math.min(100, percent)).toFixed(percent % 1 === 0 ? 0 : 1) + "%";
    }

    public statusLabel(value: any): string {
        const status = String(value || "").toLowerCase();
        if (["connected", "ready", "success", "ok"].includes(status)) return "연결됨";
        if (["completed", "profiled"].includes(status)) return "분석 완료";
        if (["failed", "error", "disconnected"].includes(status)) return "실패";
        return value ? String(value) : "기록 없음";
    }

    public mysqlStatusLabel(): string {
        return this.mysqlStatus === 'connected' ? '연결됨' : '확인 불가';
    }

    public mysqlStatusClass(): string {
        return this.mysqlStatus === 'connected'
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-amber-50 text-amber-800';
    }

    public activeOntologyLabel(): string {
        return String(
            this.activeOntology?.name
            || this.activeOntology?.display_name
            || this.activeOntology?.id
            || '활성 온톨로지 없음'
        );
    }

    public activeOntologyVersion(): string {
        const version = this.activeOntology?.version;
        return version === undefined || version === null || version === ''
            ? '버전 미상'
            : 'v' + String(version);
    }

    private applyPipelinePayload(payload: any) {
        const knowledge = payload?.knowledge && typeof payload.knowledge === 'object'
            ? payload.knowledge
            : {};
        const quality = knowledge?.quality && typeof knowledge.quality === 'object'
            ? knowledge.quality
            : {};
        const qualityCounts = quality?.counts && typeof quality.counts === 'object'
            ? quality.counts
            : {};
        const runCounts = quality?.run_counts
            || quality?.extraction_run_counts
            || knowledge?.run_counts
            || {};
        const statusCounts = quality?.status_counts
            || quality?.candidate_status_counts
            || knowledge?.status_counts
            || {};

        this.knowledge = knowledge;
        this.activeOntology = knowledge?.ontology || quality?.ontology || null;
        this.mysqlStatus = String(payload?.mysql_status || 'unknown').toLowerCase();

        const sourceScopes = [
            qualityCounts,
            quality,
            knowledge,
            payload?.counts,
            payload?.summary,
            payload?.stats,
            payload
        ];
        const runScopes = [runCounts, qualityCounts, quality, knowledge];
        const statusScopes = [statusCounts, qualityCounts, quality, knowledge];

        const extractionRuns = this.pickScopedCount(
            runScopes,
            ['extraction_runs', 'extraction_run', 'run_count', 'total_runs']
        );
        const failedRuns = this.pickScopedCount(
            runScopes,
            ['failed_runs', 'failure_runs', 'extraction_runs_failed']
        );
        const explicitSuccessfulRuns = this.pickOptionalCount(
            runScopes,
            ['successful_runs', 'success_runs', 'completed_runs', 'extraction_runs_success']
        );

        this.pipelineCounts = {
            sourceDocuments: this.pickScopedCount(
                sourceScopes,
                ['source_documents', 'active_documents', 'documents', 'document_count']
            ),
            activeDocumentVersions: this.pickScopedCount(
                sourceScopes,
                ['active_document_versions', 'current_document_versions', 'active_versions']
            ),
            activeChunks: this.pickScopedCount(
                sourceScopes,
                ['active_chunks', 'active_processing_chunks', 'processing_chunks', 'chunks']
            ),
            extractionRuns,
            successfulRuns: explicitSuccessfulRuns === null
                ? Math.max(0, extractionRuns - failedRuns)
                : explicitSuccessfulRuns,
            failedRuns,
            candidate: this.pickScopedCount(
                statusScopes,
                ['candidate', 'candidates', 'candidate_count']
            ),
            needsReview: this.pickScopedCount(
                statusScopes,
                ['needs_review', 'needs_review_count', 'review_required']
            ),
            approved: this.pickScopedCount(
                statusScopes,
                ['approved', 'approved_count']
            ),
            rejected: this.pickScopedCount(
                statusScopes,
                ['rejected', 'rejected_count']
            )
        };
    }

    private pickScopedCount(scopes: any[], keys: string[]): number {
        const value = this.pickOptionalCount(scopes, keys);
        return value === null ? 0 : value;
    }

    private pickOptionalCount(scopes: any[], keys: string[]): number | null {
        for (const scope of scopes) {
            if (!scope || Array.isArray(scope) || typeof scope !== 'object') continue;
            for (const key of keys) {
                if (scope[key] !== undefined && scope[key] !== null) {
                    return Math.max(0, this.toCount(scope[key]));
                }
            }
        }
        return null;
    }

    private buildCards(payload: any): DashboardCard[] {
        return [
            {
                label: "연결된 데이터 소스",
                value: this.pickCount(payload, ["connected_data_sources", "data_sources", "data_source_count", "source_count", "sources"]),
                description: "등록되어 조회 가능한 출처",
                marker: "DS",
                accentClass: "border-l-indigo-500",
                markerClass: "bg-indigo-50 text-indigo-700"
            },
            {
                label: "원문 문서·자료집",
                value: this.pickCount(payload, ["documents", "document_count", "source_documents"]),
                description: "출처 정보가 보존된 원문 문서",
                marker: "DOC",
                accentClass: "border-l-sky-500",
                markerClass: "bg-sky-50 text-sky-700"
            },
            {
                label: "문서 조각",
                value: this.pickCount(payload, ["chunks", "chunk_count", "source_chunks", "records"]),
                description: "앞뒤 순서를 가진 원문 단위",
                marker: "CH",
                accentClass: "border-l-emerald-500",
                markerClass: "bg-emerald-50 text-emerald-700"
            }
        ];
    }

    private pickCount(payload: any, keys: string[]): number {
        const scopes = [
            payload,
            payload?.summary,
            payload?.counts,
            payload?.stats
        ];

        for (const scope of scopes) {
            if (!scope || Array.isArray(scope)) continue;
            for (const key of keys) {
                if (scope[key] !== undefined && scope[key] !== null) {
                    return this.toCount(scope[key]);
                }
            }
        }

        return 0;
    }

    private toCount(value: any): number {
        if (Array.isArray(value)) return value.length;
        if (value && typeof value === 'object') {
            if (Array.isArray(value.items)) return value.items.length;
            if (value.count !== undefined) return this.toCount(value.count);
            if (value.total !== undefined) return this.toCount(value.total);
        }

        const numberValue = Number(value || 0);
        return Number.isFinite(numberValue) ? numberValue : 0;
    }

    private toArray(value: any): any[] {
        if (Array.isArray(value)) return value;
        if (Array.isArray(value?.items)) return value.items;
        if (Array.isArray(value?.rows)) return value.rows;
        return [];
    }
}

export default PageDashboardComponent;